import { SectionHeader, SectionWrap } from "@/components/Section";
import { Sparkles } from "lucide-react";

const PRINCIPLES = [
  {
    t: "Physics-aware modeling",
    d: "Constrain models with biological priors so predictions remain grounded in circuit-level reality.",
  },
  {
    t: "Interpretable by design",
    d: "Surface uncertainty and decision pathways instead of opaque outputs, so neuroscientists can verify.",
  },
  {
    t: "Probabilistic reasoning",
    d: "Quantify confidence across signals to support trustworthy, adaptive feedback over time.",
  },
  {
    t: "Built around the loop",
    d: "Designed to operate inside a continuous capture-model-feedback cycle, not as a one-shot inference.",
  },
];

export function AISection() {
  return (
    <SectionWrap id="ai">
      <SectionHeader
        num="04"
        kicker="The AI"
        title="From neural signals to actionable feedback."
        sub="Brain modeling requires more than pattern matching. Neurobolt emphasizes interpretable, physics-aware modeling that connects signal patterns with biological mechanisms."
      />

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 reveal">
          <div className="glass rounded-3xl p-8 lg:p-10 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full opacity-50"
              style={{ background: "radial-gradient(circle, rgba(99,102,241,0.4), transparent 65%)" }}
            />
            <Sparkles className="text-[#8B5CF6]" size={22} />
            <h3 className="mt-5 font-display text-white text-[28px] leading-tight font-medium">
              The platform direction combines neural data, computational modeling, and probabilistic reasoning.
            </h3>
            <p className="mt-5 text-[#94A3B8] text-[15px] leading-relaxed">
              We aim to enable feedback systems that can adapt to the individual,
              learn from longitudinal change, and remain interpretable to the
              scientists and clinicians who guide them.
            </p>

            {/* Mock signal viz */}
            <div className="mt-8">
              <SignalViz />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          {PRINCIPLES.map((p, i) => (
            <div
              key={i}
              className="reveal glass rounded-2xl p-7 hover:border-[#38BDF8]/30 transition-colors"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#475569]">
                0{i + 1}
              </div>
              <h4 className="mt-4 font-display text-white text-[19px] font-medium">
                {p.t}
              </h4>
              <p className="mt-2 text-[#94A3B8] text-[14px] leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionWrap>
  );
}

function SignalViz() {
  // Static SVG: stylized neural signal becoming structured output
  return (
    <svg viewBox="0 0 460 110" className="w-full h-auto">
      <defs>
        <linearGradient id="sg" x1="0" x2="1">
          <stop offset="0" stopColor="#38BDF8" />
          <stop offset="0.5" stopColor="#6366F1" />
          <stop offset="1" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
      <path
        d="M0 60 Q 10 30 20 60 T 40 60 T 60 60 T 80 60 Q 90 20 100 60 T 130 60 T 160 60 Q 180 80 200 50 Q 220 25 240 55"
        stroke="#38BDF8"
        strokeWidth="1.2"
        fill="none"
        opacity="0.7"
      />
      <path
        d="M240 55 C 260 55, 270 50, 290 50 L 320 50 L 330 40 L 340 60 L 350 30 L 360 55 L 460 55"
        stroke="url(#sg)"
        strokeWidth="1.8"
        fill="none"
      />
      {[260, 290, 320, 360, 400, 440].map((x, i) => (
        <circle key={i} cx={x} cy={55} r="2.5" fill="#fff" opacity="0.9" />
      ))}
      <text x="0" y="100" fontFamily="JetBrains Mono" fontSize="9" fill="#475569">
        RAW NEURAL ACTIVITY
      </text>
      <text x="280" y="100" fontFamily="JetBrains Mono" fontSize="9" fill="#94A3B8">
        INTERPRETABLE OUTPUT
      </text>
    </svg>
  );
}
